import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContractAPIService } from 'src/app/extensions/services/api/contracts-api.service';
import { ContractListFilterComponent } from './contract-list-filter/contract-list-filter.component';
import { Subject, Subscription } from 'rxjs';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { DEFAULT_DEBOUNCE_TIME, TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { scrollTop, setTableViewMode } from 'src/app/extensions/utilities';
import { DecimalPipe } from '@angular/common';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';

@Component({
  selector: 'app-contract-list',
  templateUrl: './contract-list.component.html',
  styleUrls: ['./contract-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class ContractListComponent implements OnInit, OnDestroy {
  @ViewChild('contractSearch') public contractSearchControl: TextBoxComponent;

  contracts: app.models.ContractModel[] = [];
  contractsProps: app.models.DynamicPropsDictionary;

  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  activeFilterCount = 0;
  public hasData = true;
  public subscriptions: Subscription[] = [];

  public contractsFilter: app.models.ContractsFilterModel;
  windowSizeLgUp: boolean;
  searchQuery$ = new Subject<app.models.SearchEvent>();

  private pageIndex = 1;
  private maxIndex: number;
  showBackToTop: boolean = false;
  initialFilterState: app.models.ContractsFilterModel;

  constructor(
    private _globalService: GlobalService,
    private _router: Router,
    private _contractsAPIService: ContractAPIService,
    private _ngModal: NgbModal,
    private _windowEventService: WindowEventsService,
    private _scrollService: ScrollService,
    private _decimalPipe: DecimalPipe
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  ngOnInit() {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute, TABLE_VIEW_MODES.CARDS_VIEW);

    this.contractsFilter = {
      activeContracts: true,
      terminationUntilDate: null,
      contractTypeCodes: [],
      contractAssetIds: [],
      isPaid: undefined,
      isNotPaid: undefined,
      isOverdue: undefined,
      searchText: '',
    } as app.models.ContractsFilterModel;
    this.initialFilterState = structuredClone(this.contractsFilter);

    this.activeFilterCount = 0;
    this.loadPagedContracts(this.contractsFilter);

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(isBackToTopDisplayed => {
      this.showBackToTop = isBackToTopDisplayed;
    });
    this.subscriptions.push(backToTopSubscription);

    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._contractsAPIService.getPagedContracts(this.pageIndex, this.contractsFilter).subscribe(response => {
          this.contracts = this.contracts.concat(this.processContractsOverdue(response.data.values));
          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(scrollSubscription);

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let subscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(subscription);

    let searchSubscription = this.searchQuery$
      .pipe(
        debounceTime(DEFAULT_DEBOUNCE_TIME),
        distinctUntilChanged((prev, curr) => {
          if (curr.force) return false;
          return prev.value === curr.value;
        })
      )
      .subscribe(res => {
        this.contractSearchControl.blur();
        this.contractsFilter.searchText = res.value;
        this.initialFilterState.searchText = res.value;
        this.loadPagedContracts(this.contractsFilter, true);
      });
    this.subscriptions.push(searchSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadPagedContracts(filter: app.models.ContractsFilterModel, forcedFocusSearchBar: boolean = false) {
    this.pageIndex = 1;
    this._contractsAPIService.getPagedContracts(this.pageIndex, filter).subscribe(response => {
      this.contracts = this.processContractsOverdue(response.data.values);
      this.contractsProps = response.props;
      this.hasData = response.data.totalCount > 0;
      this.maxIndex = response.data.totalPages;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;

      if (forcedFocusSearchBar) {
        this.contractSearchControl.focus();
      }
    });
  }

  processContractsOverdue(contracts: app.models.ContractModel[]) {
    return contracts.map(contract => ({
      ...contract,
      hasOverdue: contract.overdueAmounts.some(amount => amount.value > 0),
      overdueAmountsString: contract.overdueAmounts
        .map(amount => this._decimalPipe.transform(amount.value, '1.2-2') + ' ' + amount.code)
        .join(', '),
    }));
  }

  redirectDetails(id: string) {
    if (id) {
      this._router.navigate(['contracts', id]);
    }
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  toggleContractListState() {
    this.contractsFilter = {
      activeContracts: !this.contractsFilter.activeContracts,
      terminationUntilDate: null,
      contractTypeCodes: [],
      contractAssetIds: [],
      isPaid: undefined,
      isNotPaid: undefined,
      isOverdue: undefined,
      searchText: '',
    } as app.models.ContractsFilterModel;
    this.initialFilterState = structuredClone(this.contractsFilter);

    this.activeFilterCount = 0;
    this.pageIndex = 1;
    this.searchQuery$.next({ value: '', force: true } as app.models.SearchEvent);
  }

  resetFiltersHandler() {
    this.contractsFilter = {
      activeContracts: true,
      terminationUntilDate: null,
      contractTypeCodes: [],
      contractAssetIds: [],
      isPaid: undefined,
      isNotPaid: undefined,
      isOverdue: undefined,
      searchText: '',
    } as app.models.ContractsFilterModel;
    this.initialFilterState = structuredClone(this.contractsFilter);

    this.activeFilterCount = 0;
    this.pageIndex = 1;
    this.searchQuery$.next({ value: '', force: true } as app.models.SearchEvent);
  }

  public filterHandler() {
    let modal = this._ngModal.open(ContractListFilterComponent, {
      scrollable: true,
    });
    modal.componentInstance.contractsFilter = this.contractsFilter;
    modal.componentInstance.initialFilterState = this.initialFilterState;
    modal.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = structuredClone(this.contractsFilter);
        this.activeFilterCount = result;
        this.loadPagedContracts(this.contractsFilter);
      }
    });
  }

  scrollToTop(): void {
    scrollTop();
  }
}
